import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import { Provider as ReduxProvider } from 'react-redux';
import { ClerkProvider } from '@clerk/clerk-react';
import './lib/browser-compat';
import './i18n';
import { App } from './app/app';
import { AppToolPalette } from './features/tools-app/studio-agent';
import { ClerkTokenSync } from './api/ClerkTokenSync';
import { ClerkSignupTelemetry } from './components/ClerkSignupTelemetry';
import { LoginGateUnavailableNotice } from './components/login-gate-unavailable-notice';
import { PostHogAnalytics } from './components/PostHogAnalytics';
import { isAppHost } from './lib/host';
import { ensureDesignSystemTokens } from './lib/design-system';
import { initGoogleAnalytics } from './lib/ga';
import { installPreloadErrorRecovery } from './lib/chunk-recovery';
import { store } from './app/store';
import './design-tokens.generated.css';
import './index.css';

ensureDesignSystemTokens();
installPreloadErrorRecovery();
// Defers all work to after window load + idle — never blocks bootstrap.
initGoogleAnalytics();

const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

function RoutedApp() {
  const navigate = useNavigate();

  // Allow dev boot without Clerk so the marketing surface can be iterated
  // on without the auth layer. Auth-gated routes render a placeholder when
  // ClerkProvider is absent. PostHogAnalytics must render INSIDE ClerkProvider
  // when Clerk is enabled — its ClerkIdentify child calls useUser().
  if (!publishableKey) {
    return (
      <>
        <PostHogAnalytics hasClerk={false} />
        <App />
        <AppToolPalette />
      </>
    );
  }

  // The in-app /sign-up + /sign-in routes only exist on the app host
  // (isAppHost). On the marketing host, leave the Clerk URLs unset so
  // RedirectToSignIn etc. fall back to hosted Clerk (original behavior) rather
  // than routing to okrapdf.com/sign-in, which has no such route.
  const appHost = isAppHost();

  return (
    <ClerkProvider
      publishableKey={publishableKey}
      routerPush={(to) => navigate(to)}
      routerReplace={(to) => navigate(to, { replace: true })}
      signInUrl={appHost ? '/sign-in' : undefined}
      signUpUrl={appHost ? '/sign-up' : undefined}
      signInFallbackRedirectUrl={appHost ? '/tools' : undefined}
      signUpFallbackRedirectUrl={appHost ? '/tools' : undefined}
    >
      <ClerkTokenSync />
      <ClerkSignupTelemetry />
      <PostHogAnalytics hasClerk />
      <App />
      <AppToolPalette />
      <LoginGateUnavailableNotice />
    </ClerkProvider>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ReduxProvider store={store}>
      <BrowserRouter>
        <RoutedApp />
      </BrowserRouter>
    </ReduxProvider>
  </React.StrictMode>,
);
